import { useState, useEffect, useCallback } from 'react';
import { NotificacaoInterface } from '../types/NotificacaoInterface';
import { 
  obterTodasNotificacoes, 
  obterNotificacoesNaoLidas, 
  marcarNotificacaoComoLida,
  verificarStatusNotificacoes
} from '../services/notificacoesService';
import axios from 'axios';
import { getSessionData } from '../utils/storageUtils';

interface UseNotificacoesProps {
  carregarAutomaticamente?: boolean;
  apenasNaoLidas?: boolean;
  intervaloAtualizacao?: number; // em milissegundos
}

interface UseNotificacoesReturn {
  notificacoes: NotificacaoInterface[];
  carregando: boolean;
  erro: Error | null;
  totalNaoLidas: number;
  totalPaginas: number;
  totalNotificacoes: number;
  carregarNotificacoes: (apenasNaoLidas?: boolean) => Promise<void>;
  marcarComoLida: (id: number) => Promise<void>;
  limparErro: () => void;
  verificarConexao: () => Promise<{ status: string }>;
}

export const useNotificacoes = ({
  carregarAutomaticamente = true,
  apenasNaoLidas = false,
  intervaloAtualizacao = 60000, // 1 minuto por padrão
}: UseNotificacoesProps = {}): UseNotificacoesReturn => {
  const [notificacoes, setNotificacoes] = useState<NotificacaoInterface[]>([]);
  const [carregando, setCarregando] = useState<boolean>(false);
  const [erro, setErro] = useState<Error | null>(null);
  const [totalNaoLidas, setTotalNaoLidas] = useState<number>(0);
  const [totalPaginas, setTotalPaginas] = useState<number>(0);
  const [totalNotificacoes, setTotalNotificacoes] = useState<number>(0);

  const limparErro = useCallback(() => {
    setErro(null);
  }, []);

  const verificarConexao = useCallback(async () => {
    return await verificarStatusNotificacoes();
  }, []);

  const carregarNotificacoes = useCallback(async (apenasNaoLidasParam?: boolean) => {
    const deveCarregarApenasNaoLidas = apenasNaoLidasParam !== undefined ? apenasNaoLidasParam : apenasNaoLidas;
    
    try {
      // Verificar se o usuário está logado
      const usuarioId = getSessionData("DadosUsuarioID");
      const token = getSessionData("xc-helpdesk-token");
      
      if (!usuarioId || !token) {
        setErro(new Error('Usuário não autenticado. Faça login novamente.'));
        return;
      }
      
      setCarregando(true);
      setErro(null);
      
      const resultado = deveCarregarApenasNaoLidas 
        ? await obterNotificacoesNaoLidas()
        : await obterTodasNotificacoes();
      
      setNotificacoes(resultado.data);
      setTotalPaginas(resultado.totalPages);
      setTotalNotificacoes(resultado.total);
      
      // Atualiza o contador de não lidas
      if (!deveCarregarApenasNaoLidas) {
        const naoLidas = resultado.data.filter(notificacao => !notificacao.lida);
        setTotalNaoLidas(naoLidas.length);
      } else {
        setTotalNaoLidas(resultado.total);
      }
    } catch (error: unknown) {
      console.error('Erro ao carregar notificações:', error);
      
      // Verifica se é um erro de resposta da API
      if (axios.isAxiosError(error) && error.response) {
        const mensagem = error.response.data?.message || `Erro ${error.response.status}: ${error.response.statusText}`;
        setErro(new Error(mensagem));
      } else if (axios.isAxiosError(error) && error.request) {
        // A requisição foi feita mas não houve resposta
        setErro(new Error('Não foi possível conectar ao servidor. Verifique sua conexão.'));
      } else {
        // Erro na configuração da requisição
        setErro(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setCarregando(false);
    }
  }, [apenasNaoLidas]);

  const marcarComoLida = useCallback(async (id: number) => {
    try {
      // Verificar se o usuário está logado
      const usuarioId = getSessionData("DadosUsuarioID");
      const token = getSessionData("xc-helpdesk-token");
      
      if (!usuarioId || !token) {
        setErro(new Error('Usuário não autenticado. Faça login novamente.'));
        return;
      }
      
      setCarregando(true);
      await marcarNotificacaoComoLida(id);
      
      // Atualiza o estado local
      setNotificacoes(prevNotificacoes => 
        prevNotificacoes.map(notificacao => 
          notificacao.id === id ? { ...notificacao, lida: true } : notificacao
        )
      );
      
      // Atualiza o contador de não lidas
      setTotalNaoLidas(prev => Math.max(0, prev - 1));
    } catch (error: unknown) {
      console.error('Erro ao marcar notificação como lida:', error);
      
      // Verifica se é um erro de resposta da API
      if (axios.isAxiosError(error) && error.response) {
        const mensagem = error.response.data?.message || `Erro ${error.response.status}: ${error.response.statusText}`;
        setErro(new Error(mensagem));
      } else if (axios.isAxiosError(error) && error.request) {
        // A requisição foi feita mas não houve resposta
        setErro(new Error('Não foi possível conectar ao servidor. Verifique sua conexão.'));
      } else {
        // Erro na configuração da requisição
        setErro(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setCarregando(false);
    }
  }, []);

  // Efeito para carregar notificações automaticamente
  useEffect(() => {
    if (carregarAutomaticamente) {
      carregarNotificacoes();
    }
  }, [carregarAutomaticamente, carregarNotificacoes]);

  // Efeito para atualizar notificações periodicamente
  useEffect(() => {
    if (intervaloAtualizacao > 0) {
      const intervalo = setInterval(() => {
        carregarNotificacoes();
      }, intervaloAtualizacao);

      return () => clearInterval(intervalo);
    }
  }, [intervaloAtualizacao, carregarNotificacoes]);

  return {
    notificacoes,
    carregando,
    erro,
    totalNaoLidas,
    totalPaginas,
    totalNotificacoes,
    carregarNotificacoes,
    marcarComoLida,
    limparErro,
    verificarConexao
  };
}; 